
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ref, watch, onUnmounted } from 'vue'

gsap.registerPlugin(ScrollTrigger)

// "From" state per animation — the tween runs from here to the element's natural
// CSS, so the resting state is always the normal, visible layout.
const FROM = {
  'fade-up':     { opacity: 0, y: 40 },
  'fade-in':     { opacity: 0 },
  'slide-left':  { opacity: 0, x: 60 },  // starts right, slides left into place
  'slide-right': { opacity: 0, x: -60 }, // starts left, slides right into place
  'scale-in':    { opacity: 0, scale: 0.9 },
}

/**
 * @typedef {Object} ScrollRevealOptions
 * @property {'fade-up'|'fade-in'|'slide-left'|'slide-right'|'scale-in'} [animation='fade-up']
 *   Which entrance the element plays.
 * @property {boolean} [twoWay=false]
 *   Reverse the animation on scroll up (toggleActions 'play reverse play reverse').
 *   Ignored when `scrub` is set (scrub is inherently two-way).
 * @property {boolean|number} [scrub=false]
 *   Tie progress to the scrollbar. `true` locks 1:1; a number adds that many
 *   seconds of catch-up smoothing.
 * @property {boolean|number} [stagger=false]
 *   Animate the element's direct children in sequence. `true` uses 0.12s between
 *   children; a number sets the gap in seconds.
 * @property {string} [start='top 80%']
 *   ScrollTrigger start position ("trigger viewport").
 * @property {number} [duration=0.8]
 *   Tween duration in seconds (ignored when `scrub` drives progress).
 * @returns {import('vue').Ref<HTMLElement|null>} Template ref to bind via `ref=`.
 */
export function useScrollReveal(options = {}) {
  const {
    animation = 'fade-up',
    twoWay = false,
    scrub = false,
    stagger = false,
    start = 'top 80%',
    duration = 0.8,
  } = options

  const el = ref(null)
  // gsap.matchMedia gates everything behind prefers-reduced-motion and owns the
  // teardown: reverting it reverts every context/tween created inside add().
  const mm = gsap.matchMedia()
  let built = false

  watch(
    el,
    (node) => {
      if (!node || built) return
      built = true

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        const ctx = gsap.context(() => {
          const targets = stagger ? node.children : node
          const from = FROM[animation] || FROM['fade-up']

          gsap.from(targets, {
            ...from,
            duration,
            ease: 'power2.out',
            stagger: stagger ? (typeof stagger === 'number' ? stagger : 0.12) : 0,
            scrollTrigger: {
              trigger: node,
              start,
              ...(scrub
                ? { scrub: typeof scrub === 'number' ? scrub : true, end: 'bottom 60%' }
                : { toggleActions: twoWay ? 'play reverse play reverse' : 'play none none none' }),
            },
          })
        }, node)

        // The target may have just been added to the DOM (async data); recompute
        // every trigger's start/end against the new document height.
        ScrollTrigger.refresh()

        // matchMedia cleanup → reverts this context when the query stops matching
        // or the component unmounts.
        return () => ctx.revert()
      })
    },
    { immediate: true, flush: 'post' }
  )

  onUnmounted(() => mm.revert())

  return el
}

/** Recompute all ScrollTrigger positions — call after async content loads. */
export function refreshScrollReveal() {
  ScrollTrigger.refresh()
}
